/**
 * HistoryLimitToggle
 * Mutually exclusive toggle between "by runs" and "by messages" history modes.
 * Agno SDK enforces mutual exclusion: only one of num_history_runs / num_history_messages
 * can be non-null at a time.
 */

import React from "react";

type HistoryMode = "runs" | "messages";

interface HistoryLimitToggleProps {
  numHistoryMessages: number | null;
  onChange: (name: string, value: any) => void;
  disabled?: boolean;
}

const OPTIONS: { value: HistoryMode; label: string; hint: string }[] = [
  {
    value: "runs",
    label: "按轮次",
    hint: "限制最近 N 轮对话",
  },
  {
    value: "messages",
    label: "按消息数",
    hint: "限制最近 N 条消息（更精确控制 token）",
  },
];

export const HistoryLimitToggle: React.FC<HistoryLimitToggleProps> = ({
  numHistoryMessages,
  onChange,
  disabled = false,
}) => {
  const mode: HistoryMode = numHistoryMessages != null ? "messages" : "runs";

  const handleModeChange = (newMode: HistoryMode) => {
    if (newMode === mode || disabled) return;

    if (newMode === "runs") {
      onChange("num_history_runs", 3);
      onChange("num_history_messages", null);
    } else {
      // 按消息数模式默认 25 (基于生产 p75 分布)
      // parent onChange 会自动联动 session_summary.message_threshold / regenerate_interval
      onChange("num_history_messages", 25);
      onChange("num_history_runs", null);
    }
  };

  return (
    <div className="mb-1">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        历史窗口模式
      </label>
      <div className="flex gap-2">
        {OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            onClick={() => handleModeChange(opt.value)}
            className={`
              flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-all
              ${
                mode === opt.value
                  ? "bg-blue-50 border-blue-500 text-blue-700 ring-1 ring-blue-500"
                  : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
              }
              ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
            `}
          >
            <div>{opt.label}</div>
            <div className="text-xs font-normal mt-0.5 opacity-75">
              {opt.hint}
            </div>
          </button>
        ))}
      </div>
      <div className="text-xs text-gray-500 mt-1.5">
        Agno 互斥规则：两者只能选一。按消息数模式下，会话摘要的「首次生成阈值」和「再生成间隔」会自动与最大消息数对齐（无缝衔接，避免信息遗漏或 token 浪费）。
      </div>
    </div>
  );
};

export default HistoryLimitToggle;
